import { FC, Fragment, useEffect, useState } from 'react';
import { Button, FieldRow, Flex, Modal, Spacer, Table } from './index';

export type AssignableUserType = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
};

type AssignUsersModalProps = {
  open: boolean;
  loading?: boolean;
  users: AssignableUserType[];
  onClose: () => void;
  onAdd: (users: AssignableUserType[]) => void;
};

/** Picks users to grant access to. The caller passes only the users that aren't assigned yet. */
const AssignUsersModal: FC<AssignUsersModalProps> = props => {
  const [checked, setChecked] = useState<{ [userId: string]: boolean }>({});

  useEffect(() => {
    if (props.open) setChecked({});
  }, [props.open]);

  const checkedIds = Object.keys(checked).filter(id => checked[id]);
  const allChecked = props.users.length > 0 && checkedIds.length === props.users.length;

  const toggleUser = (userId: string) => {
    setChecked(current => ({ ...current, [userId]: !current[userId] }));
  };

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
    } else {
      const next: { [userId: string]: boolean } = {};
      props.users.forEach(user => (next[user.id] = true));
      setChecked(next);
    }
  };

  const COLUMNS = [
    {
      id: 'checkbox',
      sortable: false,
      width: 45,
      Header: () => (
        <input
          type="checkbox"
          className="checkbox"
          checked={allChecked}
          ref={input => {
            if (input) input.indeterminate = !allChecked && checkedIds.length > 0;
          }}
          onChange={() => toggleAll()}
        />
      ),
      Cell: (row: any) => <input type="checkbox" readOnly checked={checked[row.original.id] === true} />,
    },
    { id: 'firstName', Header: 'Name', accessor: (row: AssignableUserType) => row.firstName || '' },
    { id: 'lastName', Header: 'Last Name', accessor: (row: AssignableUserType) => row.lastName || '' },
    { id: 'email', Header: 'Email', accessor: (row: AssignableUserType) => row.email },
  ];

  return (
    <Fragment>
      <Modal open={props.open} onClose={() => props.onClose()} heading={'Add User'} width="900px">
        {props.open && (
          <Flex direction="column" size="1" px={3}>
            <Table
              hideActionButton={true}
              heading=""
              columns={COLUMNS}
              data={props.users}
              defaultPageSize={5}
              loading={props.loading}
              onRowClick={row => toggleUser(row.original.id)}
            />
            <Spacer spacing={20} />
            <FieldRow justifyContent="flex-end">
              <Flex direction="row" size="1" justify="flex-end">
                <Flex direction="row" size="0.5" flexWrap="nowrap">
                  <Button type="button" text="Cancel" mr={3} onClick={() => props.onClose()} />
                  <Button
                    type="button"
                    text="Add"
                    disabled={checkedIds.length === 0}
                    onClick={() => {
                      props.onAdd(props.users.filter(user => checked[user.id] === true));
                      props.onClose();
                    }}
                  />
                </Flex>
              </Flex>
            </FieldRow>
          </Flex>
        )}
      </Modal>
    </Fragment>
  );
};

export default AssignUsersModal;
